import { useStore } from 'vuex';
import Swal from 'sweetalert2';
import 'sweetalert2/src/sweetalert2.scss';

/**
 * Composable para gestionar la API de archivos de usuario
 * @returns {Object} - Métodos para gestionar archivos de usuario
 */
export function useUserFilePlatformAPI() {
  const store = useStore();

  /**
   * Obtiene la lista de archivos de una postulación
   * @param {Object} platformData - Datos de la postulación
   * @returns {Promise<Array>} - Lista de archivos
   */
  const getUserFiles = async (platformData) => {
    if (platformData.id_type_status === 3) {
      return [];
    }

    const params = {
      url: "user_file_platform/all",
      params: {
        active: 1,
        id_user_platform: platformData.id,
        order_by: "id",
        order_value: "asc",
      },
    };

    try {
      const response = await store.dispatch('getData', params);
      if (response.code === 200) {
        return response.data;
      }
      return [];
    } catch (error) {
      console.error('Error loading user files:', error);
      return [];
    }
  };

  /**
   * Sube archivos de usuario
   * @param {Array} filePondInstance - Instancia de FilePond con los archivos
   * @param {number} platformId - ID de la postulación
   * @returns {Promise<boolean>} - True si se subieron correctamente
   */
  const uploadFiles = async (filePondInstance, platformId) => {
    if (!filePondInstance || filePondInstance.length === 0) {
      Swal.fire({
        title: "No hay archivos para subir",
        icon: "warning",
        showConfirmButton: true,
      });
      return false;
    }

    Swal.fire({
      title: "Subiendo archivos...",
      icon: "info",
      showConfirmButton: false,
      allowOutsideClick: false,
      onBeforeOpen: () => {
        Swal.showLoading();
      },
    });

    const promises = [];
    for (let i = 0; i < filePondInstance.length; i++) {
      const formData = new FormData();
      const file = filePondInstance[i].file;
      const fileName = file.name;
      formData.append("url_file", file);
      formData.append("name", fileName);
      formData.append("id_user_platform", platformId);

      promises.push(
        store.dispatch('createData', {
          url: "user_file_platform/save",
          data: formData,
        })
      );
    }

    try {
      await Promise.all(promises);
      Swal.close();
      Swal.fire({
        title: "Postulación actualizada correctamente",
        text: "Ahora debes esperar a que respondan tu postulación",
        icon: "success",
        showConfirmButton: true,
      });
      return true;
    } catch (error) {
      console.error('Error uploading files:', error);
      Swal.close();
      Swal.fire({
        title: "Error",
        text: "Ocurrió un error al subir los archivos",
        icon: "error",
        showConfirmButton: true,
      });
      return false;
    }
  };

  /**
   * Elimina un archivo de usuario
   * @param {number} fileId - ID del archivo a eliminar
   * @returns {Promise<boolean>} - True si se eliminó correctamente
   */
  const deleteUserFile = async (fileId) => {
    const params = {
      url: "user_file_platform/delete",
      data: { id: fileId },
    };

    try {
      const response = await store.dispatch('deleteData', params);
      if (response.code === 200) {
        Swal.fire({
          title: "Archivo eliminado correctamente",
          icon: "success",
          showConfirmButton: true,
        });
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error deleting user file:', error);
      Swal.fire({
        title: "Error",
        text: "Ocurrió un error al eliminar el archivo",
        icon: "error",
        showConfirmButton: true,
      });
      return false;
    }
  };

  return {
    getUserFiles,
    uploadFiles,
    deleteUserFile,
  };
}
